import './styles.scss'
import React, { useState, useContext, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min'
import { popupIcon } from 'Assets/Icons'
import { CoffeeWebContext } from 'Context/CoffeeWebContext'
import apiAdapterCoffeeWeb from 'Services/apiAdapter_CoffeeWeb'
import { getLocalLoggedInStatus, getLocalUserDetails } from 'Utils/LocalStorage_Handler'
import HistoryTerminalDataModal from 'Screens/CoffeeQuotes/Components/MacroSignals/components/HistoryTerminalDataModal'
import { coffeeReportJsonLinks } from 'Utils/Constants'
import { commonMethods } from 'Utils/commonMethods'
import HistoryCertifiedLotsDataModal from './components/HistoryCertifiedLotsDataModal'
import microSignalsUtils from './utils/microSignals.utils'

const MacroSignals = (props) => {
  const { volumeDataRef, certifiedLotsDataRef } = props
  const location = useLocation()
  const { t } = useTranslation()

  const fullPath = location.pathname
  const pathSegments = fullPath.split('/')
  const desiredPath = pathSegments[2] || ''

  const userDetails = getLocalUserDetails()
  const isUserLoggedIn = getLocalLoggedInStatus()

  const { setLoading, realtimeSubscriptionData } = useContext(CoffeeWebContext)

  const { robustaTotalCertifiedStocks, arabicaTotalCertifiedStocks } = coffeeReportJsonLinks
  const { checkForUserAccess } = commonMethods
  const { formatNumber, formatDate, getPendingData } = microSignalsUtils

  let menuLock

  if (userDetails && userDetails.businessGeneralMenus?.businessMenu) {
    const menu = userDetails.businessGeneralMenus?.businessMenu?.find(({ menuUrl }) => menuUrl?.includes(desiredPath))

    if (menu) {
      menuLock = menu.menulock
    }
  }

  const isPlatinumUser = realtimeSubscriptionData?.subscriptionDetails?.SubscriptionName === 'Platinum' || realtimeSubscriptionData?.subscriptionDetails?.SubscriptionName === 'Trial Platinum'

  const [certifiedLotsHistoryData, setCertifiedLotsHistoryData] = useState([])
  const [historyData, setHistoryData] = useState([])
  const [historyCertifiedModalTitle, setHistoryCertifiedModalTitle] = useState(null)
  const [historyModalTitle, setHistoryModalTitle] = useState(null)
  const [isCertifiedLotsHistoryPopupOpen, setIsCertifiedLotsHistoryPopupOpen] = useState(false)
  const [isHistoryPopupOpen, setIsHistoryPopupOpen] = useState(false)
  const [isApiError, setIsApiError] = useState({
    historyContract: false,
    certifiedLots: false
  })

  const fetchHistoryContractData = async (idMarket) => {
    setHistoryData([])
    setLoading(true)
    setHistoryModalTitle(idMarket)

    try {
      const response = await apiAdapterCoffeeWeb.getHistoryDataForMicroSignals(idMarket)

      setHistoryData(response?.data?.returnLst)
    } catch {
      setIsApiError((prev) => ({ ...prev, historyContract: true }))
    } finally {
      setLoading(false)
    }
  }

  const fetchHistoryCertifiedLotData = async (reportName) => {
    setLoading(true)
    setCertifiedLotsHistoryData([])
    try {
      const response = await apiAdapterCoffeeWeb.getDataByReportName({ reportName })

      setCertifiedLotsHistoryData(response?.data?.details)
    } catch {
      setIsApiError((prev) => ({ ...prev, certifiedLots: true }))
    } finally {
      setLoading(false)
    }
  }

  const handleOnClickHistoryData = (market) => {
    const idMarket = market?.type === 'robusta' ? '1' : '2'

    if (!isUserLoggedIn) return

    fetchHistoryContractData(idMarket)
    setIsHistoryPopupOpen(true)
  }

  const handleClickForCertifiedLots = (marketType) => {
    const reportName = marketType === 'robusta' ? robustaTotalCertifiedStocks : arabicaTotalCertifiedStocks

    if (!isUserLoggedIn) return

    setHistoryCertifiedModalTitle(marketType === 'robusta' ? '1' : '2')
    setIsCertifiedLotsHistoryPopupOpen(true)
    fetchHistoryCertifiedLotData(reportName)
  }

  const formatWithSignSpacing = (value) => {
    if (value == null) return ''

    const absValue = Math.abs(value)
    const formattedvalue = formatNumber(absValue)

    if (value > 0) return `+ ${formattedvalue}`
    if (value < 0) return `- ${formattedvalue}`

    return formattedvalue
  }

  const getCellClass = (value) => {
    if (value > 0) return 'green'
    if (value < 0) return 'red'

    return 'neutral'
  }

  return (
    <div className="terminal-wrapper" style={checkForUserAccess(menuLock)}>
      {/* ====== VOLUME DATA ====== */}
      {volumeDataRef?.current?.map((section, index) => (
        <div key={index} className="terminal-section">
          <div className="terminal-title">{section?.title}</div>

          <div className="terminal-table-wrapper">
            <table className="terminal-table">
              <thead>
                <tr>
                  <th>{t('DATE')}</th>
                  <th>{t('VOLUME')}</th>
                  <th>{t('DIFFERENCE')}</th>
                  <th>{t('OPEN_INTEREST')}</th>
                  <th>{t('DIFFERENCE')}</th>
                </tr>
              </thead>

              <tbody>
                {section?.records?.map((item, index) => (
                  <tr key={index} onClick={() => handleOnClickHistoryData(section)}>
                    <td className="macro-signals-date">
                      <div className="date-with-icon">
                        <div>{item?.date}</div>
                        <img className="popup-icon" src={popupIcon} alt="popup-icon" />
                      </div>
                    </td>

                    <td className={item?.volumeDifference < 0 ? 'red' : 'green'}>{formatNumber(item?.volume)}</td>
                    <td className={item?.volumeDifference < 0 ? 'red' : 'green'}>{formatWithSignSpacing(item?.volumeDifference)}</td>
                    <td className={item?.openInterestDifference < 0 ? 'red' : 'green'}>{formatNumber(item?.openInterest)}</td>
                    <td className={item?.openInterestDifference < 0 ? 'red' : 'green'}>{formatWithSignSpacing(item?.openInterestDifference)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {/* ======== CERTIFIED LOTS ======= */}
      {certifiedLotsDataRef?.current?.records?.length > 0 && (
        <div className="terminal-section">
          <div className="terminal-title">{t(certifiedLotsDataRef.current.title)}</div>

          <div className="terminal-table-wrapper certified-lots-table-wrapper">
            <table className="terminal-table certified-lots-table">
              <tbody>
                {certifiedLotsDataRef.current.records.map((item) => {
                  const cellClass = getCellClass(item.comparedToPreDay)

                  return (
                    <tr key={item.type} onClick={() => handleClickForCertifiedLots(item.type)}>
                      <td className="macro-signals-date">
                        <div className="date-with-icon">
                          <td className={cellClass}>{formatDate(item.date)}</td>
                          <img className="popup-icon" src={popupIcon} alt="popup-icon" />
                        </div>
                      </td>
                      <td className={cellClass}>{item?.label}</td>
                      <td className={cellClass}>{formatNumber(Math.round(item.certifiedStocks))}</td>
                      <td className={cellClass}>{formatNumber(Math.round(item.comparedToPreDay))}</td>
                      <td className="pending-and-suspended-td">{item.type === 'robusta' ? `NON-TEN: ${formatNumber(item?.nonTenderable)}` : getPendingData(item)}</td>{' '}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ====== HISTORY DATA MODALS ======== */}
      <HistoryTerminalDataModal historyDataList={historyData} historyModalTitle={historyModalTitle} isHistoryPopupOpen={isHistoryPopupOpen} setIsHistoryPopupOpen={setIsHistoryPopupOpen} formatNumber={formatNumber} isApiError={isApiError.historyContract} />
      <HistoryCertifiedLotsDataModal certifiedLotsHistoryData={certifiedLotsHistoryData} historyCertifiedModalTitle={historyCertifiedModalTitle} isCertifiedLotsHistoryPopupOpen={isCertifiedLotsHistoryPopupOpen} setIsCertifiedLotsHistoryPopupOpen={setIsCertifiedLotsHistoryPopupOpen} formatNumber={formatNumber} checkForUserAccess={checkForUserAccess} isPlatinumUser={isPlatinumUser} isApiError={isApiError.certifiedLots} />
    </div>
  )
}

export default MacroSignals
